import React, { useState, useRef } from 'react';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * FIBA half-court zones for automatic zone detection.
 * Coordinates are percentages (0-100) of the court dimensions.
 * Court orientation: basket at bottom center.
 */
const ZONES = [
  { id: 'paint', label: 'Paint', path: 'rect', x: 31, y: 72, w: 38, h: 28 },
  { id: 'mid-left', label: 'Mid L', check: (x, y) => x < 31 && y >= 55 },
  { id: 'mid-right', label: 'Mid R', check: (x, y) => x > 69 && y >= 55 },
  { id: 'mid-top', label: 'Mid Top', check: (x, y) => x >= 31 && x <= 69 && y >= 55 && y < 72 },
  { id: '3pt-left-corner', label: '3 Corner L', check: (x, y) => x < 15 && y >= 75 },
  { id: '3pt-right-corner', label: '3 Corner R', check: (x, y) => x > 85 && y >= 75 },
  { id: '3pt-left-wing', label: '3 Wing L', check: (x, y) => x < 25 && y >= 40 && y < 75 },
  { id: '3pt-right-wing', label: '3 Wing R', check: (x, y) => x > 75 && y >= 40 && y < 75 },
  { id: '3pt-top', label: '3 Top', check: (x, y) => y < 40 },
];

function detectZone(x, y) {
  // Paint check first (highest priority)
  const paint = ZONES[0];
  if (x >= paint.x && x <= paint.x + paint.w && y >= paint.y) {
    return 'paint';
  }
  // Then check other zones
  for (let i = 1; i < ZONES.length; i++) {
    if (ZONES[i].check && ZONES[i].check(x, y)) {
      return ZONES[i].id;
    }
  }
  return 'mid-range';
}

/**
 * Interactive half-court overlay for recording shot positions.
 * Props:
 *   - onShotRecorded: (shot) => void — called with {court_x, court_y, zone, made}
 *   - onClose: () => void
 *   - playerName: string
 *   - actionCode: string (e.g. '2PTS_MADE')
 *   - made: boolean
 *   - existingShots: array of {court_x, court_y, made} for display
 */
export default function ShotChartOverlay({
  onShotRecorded,
  onClose,
  playerName = '',
  actionCode = '',
  made = true,
  existingShots = []
}) {
  const svgRef = useRef(null);
  const [lastTap, setLastTap] = useState(null);

  const handleClick = (e) => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const zone = detectZone(x, y);
    const shot = {
      court_x: Math.round(x * 100) / 100,
      court_y: Math.round(y * 100) / 100,
      zone,
      made,
    };

    setLastTap(shot);

    if (onShotRecorded) {
      onShotRecorded(shot);
    }
  };

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      maxWidth: 400,
      mx: 'auto',
      bgcolor: 'rgba(0,0,0,0.85)',
      borderRadius: 2,
      p: 1,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
            {playerName}
          </Typography>
          <Chip
            label={made ? 'Made' : 'Missed'}
            size="small"
            sx={{
              ml: 1,
              height: 20,
              fontSize: '10px',
              bgcolor: made ? 'success.main' : 'error.main',
              color: 'white'
            }}
          />
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ color: 'grey.400' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* FIBA Half Court SVG */}
      <svg
        ref={svgRef}
        viewBox="0 0 500 470"
        onClick={handleClick}
        style={{
          width: '100%',
          cursor: 'crosshair',
          borderRadius: 8,
          backgroundColor: '#2d5a27',
        }}
      >
        {/* Court outline */}
        <rect x="10" y="10" width="480" height="450" fill="none" stroke="white" strokeWidth="2" />

        {/* 3-point line (FIBA: 6.75m radius, corners at 0.9m from sideline) */}
        <path
          d="M 75 460 L 75 310 A 170 170 0 0 1 425 310 L 425 460"
          fill="none" stroke="white" strokeWidth="2"
        />

        {/* Paint / Key (FIBA: 4.9m wide, 5.8m long) */}
        <rect x="155" y="310" width="190" height="150" fill="none" stroke="white" strokeWidth="2" />

        {/* Free throw circle */}
        <circle cx="250" cy="310" r="60" fill="none" stroke="white" strokeWidth="2" strokeDasharray="8 8" />
        <path d="M 190 310 A 60 60 0 0 1 310 310" fill="none" stroke="white" strokeWidth="2" />

        {/* Restricted area (FIBA: 1.25m radius) */}
        <path
          d="M 220 460 A 30 30 0 0 1 280 460"
          fill="none" stroke="white" strokeWidth="2"
        />

        {/* Basket */}
        <circle cx="250" cy="445" r="8" fill="none" stroke="#ff6600" strokeWidth="2.5" />
        {/* Backboard */}
        <line x1="220" y1="435" x2="280" y2="435" stroke="#ff6600" strokeWidth="3" />

        {/* Center court line (top) */}
        <line x1="10" y1="10" x2="490" y2="10" stroke="white" strokeWidth="2" />

        {/* Existing shots */}
        {existingShots.map((shot, i) => (
          <g key={i}>
            {shot.made ? (
              <circle
                cx={shot.court_x * 5}
                cy={shot.court_y * 4.7}
                r="6"
                fill="#4caf50"
                fillOpacity="0.7"
                stroke="white"
                strokeWidth="1"
              />
            ) : (
              <g transform={`translate(${shot.court_x * 5}, ${shot.court_y * 4.7})`}>
                <line x1="-5" y1="-5" x2="5" y2="5" stroke="#f44336" strokeWidth="2" />
                <line x1="5" y1="-5" x2="-5" y2="5" stroke="#f44336" strokeWidth="2" />
              </g>
            )}
          </g>
        ))}

        {/* Last tap indicator */}
        {lastTap && (
          <circle
            cx={lastTap.court_x * 5}
            cy={lastTap.court_y * 4.7}
            r="12"
            fill="none"
            stroke={lastTap.made ? '#4caf50' : '#f44336'}
            strokeWidth="3"
            opacity="0.8"
          >
            <animate attributeName="r" from="12" to="20" dur="0.5s" fill="freeze" />
            <animate attributeName="opacity" from="0.8" to="0" dur="0.5s" fill="freeze" />
          </circle>
        )}

        {/* Zone labels (subtle) */}
        <text x="250" y="420" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="12">PAINT</text>
        <text x="40" y="400" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="10">3PT</text>
        <text x="460" y="400" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="10">3PT</text>
        <text x="250" y="30" textAnchor="middle" fill="rgba(255,255,255,0.2)" fontSize="10">3PT TOP</text>
      </svg>

      {/* Zone info */}
      {lastTap && (
        <Typography variant="caption" sx={{ color: 'grey.400', textAlign: 'center', display: 'block', mt: 0.5 }}>
          Zone: {lastTap.zone} ({lastTap.court_x.toFixed(0)}%, {lastTap.court_y.toFixed(0)}%)
        </Typography>
      )}
    </Box>
  );
}

/**
 * Quick zone selector for LIVE mode (faster than full court tap).
 * Shows 6 zone buttons for quick selection.
 */
export function QuickZoneSelector({ onZoneSelected, made = true }) {
  const zones = [
    { id: 'paint', label: 'Paint', color: '#ff6f00', x: 50, y: 85 },
    { id: 'mid-range', label: 'Mid', color: '#2196f3', x: 50, y: 65 },
    { id: '3pt-left-corner', label: '3 Corner L', color: '#9c27b0', x: 10, y: 85 },
    { id: '3pt-right-corner', label: '3 Corner R', color: '#9c27b0', x: 90, y: 85 },
    { id: '3pt-left-wing', label: '3 Wing L', color: '#673ab7', x: 15, y: 55 },
    { id: '3pt-right-wing', label: '3 Wing R', color: '#673ab7', x: 85, y: 55 },
    { id: '3pt-top', label: '3 Top', color: '#3f51b5', x: 50, y: 30 },
  ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
      {zones.map(z => (
        <Chip
          key={z.id}
          label={z.label}
          size="small"
          onClick={() => onZoneSelected({ zone: z.id, court_x: z.x, court_y: z.y, made })}
          sx={{
            fontSize: '10px',
            height: '24px',
            bgcolor: z.color,
            color: 'white',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 }
          }}
        />
      ))}
    </Box>
  );
}
